<script>
import { HeadToHeadCardStyle } from '@/components/cms/cms-config';

export default {
  props: {
    cardStyle: { type: Number, default: HeadToHeadCardStyle.SplitBarClassic },
    chartOnly: { type: Boolean, default: false },
  },
  data() {
    return { HeadToHeadCardStyle };
  },
};
</script>

<template>
  <div
    class="h2h-loader"
    :class="{ [`h2h-loader--style-${cardStyle}`]: !chartOnly, 'h2h-loader--chart': chartOnly }"
    role="status"
    aria-busy="true"
    :aria-label="chartOnly ? 'Loading price history' : 'Loading market'"
  >
    <div aria-hidden="true">
      <template v-if="!chartOnly">
        <div class="h2h-loader__title"></div>
        <div
          v-if="cardStyle === HeadToHeadCardStyle.SummaryWithChart"
          class="h2h-loader__headline"
        ></div>
      </template>
      <div
        v-if="chartOnly || cardStyle === HeadToHeadCardStyle.SummaryWithChart"
        class="h2h-loader__chart"
      ></div>
      <template v-if="!chartOnly">
        <div class="h2h-loader__sides">
          <div v-for="side in 2" :key="side" class="h2h-loader__side">
            <div class="h2h-loader__portrait"></div>
            <div class="h2h-loader__name"></div>
            <div class="h2h-loader__price"></div>
          </div>
        </div>
        <div
          v-if="cardStyle !== HeadToHeadCardStyle.SummaryWithChart"
          class="h2h-loader__bar"
        ></div>
        <div
          v-if="
            cardStyle === HeadToHeadCardStyle.SplitImage ||
            cardStyle === HeadToHeadCardStyle.SummaryWithChart
          "
          class="h2h-loader__buttons"
        >
          <div></div>
          <div></div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@import '~styles/settings';

.h2h-loader {
  width: 100%;
  min-width: 0;
  padding: 18px 20px 20px;
  background: $white;
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  body[data-theme='dark'] & {
    background: var(--surface-raised);
  }
  // Reuse text-line geometry/tokens without fading the loading shapes to invisible.
  * {
    animation-name: h2h-loader-pulse;
  }
  &__title {
    @include text-line(65%, 18px);
    margin-bottom: 24px;
  }
  &__headline {
    @include text-line(90%, 62px);
    margin-bottom: 16px;
  }
  &__sides,
  &__buttons {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
  &__side {
    min-width: 0;
  }
  &__portrait {
    @include text-line(64px, 64px);
    margin-bottom: 10px;
  }
  &__name {
    @include text-line(80%, 14px);
    margin-bottom: 8px;
  }
  &__price {
    @include text-line(55%, 26px);
  }
  &__bar {
    @include text-line(100%, 6px);
    margin-top: 18px;
  }
  &__chart {
    @include text-line(100%, auto);
    aspect-ratio: 365 / 120;
  }
  &__buttons {
    margin-top: 16px;
    > div {
      @include text-line(100%, 48px);
    }
  }
  &--style-2 &__portrait {
    width: 100%;
    height: 250px;
  }
  &--style-3 &__portrait {
    width: 96px;
    height: 116px;
  }
  &--style-3 &__bar {
    height: 52px;
  }
  &--style-4 {
    max-width: 344px;
    padding: 20px;
    border-radius: 8px;
  }
  &--style-4 &__chart {
    margin-bottom: 16px;
  }
  &--style-4 &__portrait {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }
  &--chart {
    padding: 0;
    border: 0;
    border-radius: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none;
    }
  }
}

@keyframes h2h-loader-pulse {
  0%,
  100% {
    opacity: 0.65;
  }
  50% {
    opacity: 1;
  }
}
</style>
